<template>
    <div>
        <template>
            <slot :publicUserId="driver.publicUserId" />
        </template>
    </div>
</template>
<script>
import { ref, toRefs, watch, inject, computed } from '@vue/composition-api';
import { MapBoxMixin } from '@/mixins/MapBoxMixin';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import useMapUtils from '@/compostables/useMapUtils';

export default {
    name: 'DriverMarker',
    mixins: [MapBoxMixin, GeneralMixin],
    props: {
        driver: {
            type: Object,
            default: () => {}
        },
        markers: {
            type: Array,
            default: () => {}
        },
        isDateToday: {
            type: Boolean,
            default: true
        }
    },
    setup(props, { root }) {
        const { isDateToday, driver } = toRefs(props);
        const map = inject('map');
        const bounds = inject('bounds');
        const isStreetView = inject('isStreetView');
        const { getters, dispatch } = inject('vuex-store');

        const pinnedUser = computed(() => getters['map/pinnedUser']);

        const isPlaying = computed(() => getters['map/isPlaying']);

        const { getMarkerOpacity } = useMapUtils(root);
        const marker = ref(null);

        if (driver.value.routeDetails?.coordinates === null) 
            return marker;

        watch(driver, (newVal) => {
            if (!isDateToday.value || isStreetView.value) 
                return true;
            const {
                routeDetails: {
                    coordinates: { latitude, longitude }
                }
            } = newVal;
            const pos = [longitude, latitude];
            if (!pinnedUser.value || pinnedUser.value === newVal.publicUserId) {
                marker.value.setLngLat(pos);
            }
            return true;
        });

        return {
            map,
            marker,
            getMarkerOpacity,
            dispatch,
            pinnedUser,
            bounds,
            isPlaying,
            isStreetView
        };
    },
    render: () => null,
    mounted() {
        this.showMarker();
    },
    watch: {
        clickedStop() {
            if (this.publicUserId === this.driver.publicUserId) {
                this.fitBoundsToSelf();
            }
        },
        isStreetView(newVal) {
            if (!this.isDateToday) 
                return true;

            if (!newVal && (!this.pinnedUser || (this.pinnedUser && this.driver.publicUserId === this.pinnedUser)))
                this.addMarker();
            else {
                this.removeMarker();
            }

            return true;
        },
        isPlaying(newVal) {
            if (!newVal && this.isDateToday && (!this.pinnedUser || this.pinnedUser === this.driver.publicUserId)) {
                this.addMarker();
                this.fitBoundsToSelf();
            } else {
                this.removeMarker();
            }
        },
        isDateToday(newVal) {
            if (newVal && (!this.pinnedUser || (this.pinnedUser && this.driver.publicUserId === this.pinnedUser))) {
                this.addMarker();
                this.fitBoundsToSelf();
            } else {
                this.removeMarker();
            }
        },
        async pinnedUser(newVal) {
            // unpinning a user
            if (!newVal && this.marker && this.isDateToday) {
                this.addMarker();

                this.fitBounds(this.markers.map((x) => [x.getLngLat().lng, x.getLngLat().lat]));
            }
            // pinning a user
            else if (this.marker) {
                if (newVal === this.driver.publicUserId && this.isDateToday) {
                    this.addMarker();
                    this.fitBoundsToSelf();
                } else {
                    this.removeMarker();
                }
            }
        }
    },
    methods: {
        showMarker() {
            if (!this.driver.routeDetails?.coordinates) {
                return;
            }
            const {
                coordinates: { latitude, longitude }
            } = this.driver.routeDetails;

            const opacity = this.getMarkerOpacity(this.driver.routeDetails?.lastLocationUpdate);
            const photoUrl = this.driver.photoUrl ?? '/img/profile-pic.png';
            const customPin = { ...this.driver.markerPins, opacity, photoUrl };
            const type = 'team member';
            this.marker = this.$_mapBox_setMarkers(this.map, latitude, longitude, type, customPin);
            this.marker.userId = this.driver.publicUserId;
            this.addMarker();

            this.marker.getElement().addEventListener('click', () => {
                this.$root.$emit('marker-clicked', { data: this.driver, marker: this.marker, type: 'driver' });
            });

            const markerLngLat = this.marker.getLngLat();

            // update location.
            const onDrop = () => {
                this.dispatch('map/SET_DRIVER_LOCATION', {
                    publicUserId: this.driver.publicUserId,
                    trackerId: null,
                    location: {
                        latitude: markerLngLat.lat,
                        longitude: markerLngLat.long
                    }
                });
            };

            // eslint-disable-next-line no-underscore-dangle
            if (this.marker._draggable) {
                this.$_mapBox_handleListener(this.map, 'mouseover', () => {
                    // eslint-disable-next-line no-underscore-dangle
                    if (!this.marker?._element) {
                        return;
                    }
                    // eslint-disable-next-line no-underscore-dangle
                    const mapImageMarker = Array.from(this.marker._element.children).find(
                        (x) => x.className === 'asset-map-image-marker'
                    );
                    if (mapImageMarker) {
                        if (mapImageMarker.getAttribute('markerDropListener') !== 'true') {
                            mapImageMarker.setAttribute('markerDropListener', 'true');
                            mapImageMarker.addEventListener('click', () => {
                                onDrop();
                            });
                        }
                    }
                });
            }

            this.fitBounds(this.markers.map((x) => [x.getLngLat().lng, x.getLngLat().lat]));

            // Set map null if
            // 1. Not today 2. Other users pinned 2. Null Island
            if (
                !this.isDateToday ||
                (this.pinnedUser && this.pinnedUser !== this.driver.publicUserId) ||
                (latitude === 0 && longitude === 0)
            ) {
                this.removeMarker();
            }
        },
        fitBoundsToSelf() {
            this.fitBounds([[this.marker.getLngLat().lng, this.marker.getLngLat().lat]]);
        },
        fitBounds(bounds) {
            const coordinates = this.$_mapBox_getMaxBounds(bounds);
            this.$_mapBox_callMapFunction(this.map, 'fitBounds', coordinates, false);
        },
        removeMarker() {
            this.$emit('deleteMarker', this.marker);
        },
        addMarker() {
            if (!this.markers.some((x) => x.userId === this.marker.userId)) {
                this.markers.push(this.marker);
                this.$_mapBox_addMapElement(this.marker, this.map);
            }
        }
    }
};
</script>
